<template>
  <base-editor-page id="scoring-report" :isLoading="isLoading">
    <p slot="description">
      View how your scoring has been allocated, skill by skill. This page also contains a sample
      Feedback Report, the content that displays to participants after completing their Inbox. Some
      example data points have been given to help show how this will appear.
    </p>
    <template slot="body">
      <div class="scoring-indicator pt-15 pb-5">
        <p>
          This inbox evaluates performance using the:
          <span class="font-weight-bold">{{ scoringMethod }} Scoring Method</span>. To change the
          scoring method, go to the Gadgets page.
        </p>
      </div>

      <h2 class="mt-30">Scoring Recap</h2>
      <h3 class="font-weight-bold">Skills Overview</h3>
      <skills-overview :skillsOverview="skillsOverview" />

      <div class="row">
        <div class="col-lg-6 col-md-6 col-xs-6">
          <h3 class="font-weight-bold">Deep Dive</h3>
        </div>

        <div class="col-lg-6 col-md-6 col-xs-6 pt-15">
          <base-filter
            :qualifiers="{ skills }"
            :applyFilter="applyFilter"
            :custom-styles="{
              ul: 'min-width: 200px; transform: translateX(-54%);',
              caret: 'margin-left: 5px;',
              div: 'float: right;',
            }"
            :caret="validateCaret"
            v-slot="{ qualifier, data }"
          >
            <span v-if="qualifier === 'skills'" class="question-tag">{{ data.name }}</span>
          </base-filter>
        </div>
      </div>

      <div class="border-style">
        <div class="row fixed table-head ml-0 mr-0">
          <div class="col-lg-4 col-md-6 col-xs-6 p-10 font-weight-bold">
            <p class="ml-5 header-font-size mt-5">Emails and Instant Messages</p>
          </div>
          <div class="col-lg-8 col-md-6 col-xs-6 mt-5 pt-10 pb-10 pl-5 font-weight-bold">
            <p class="header-font-size">Skills (Max Points)</p>
          </div>
        </div>
        <div class="table-overflow">
          <template
            v-for="(
              { stimulis, answerswithSkills, combinedAnswerWithSkills }, questionKey, i
            ) in stimuliwithQuestions"
          >
            <stimuli-with-skills
              :key="`${questionKey}-${i}`"
              :questionKey="questionKey"
              :stimulis="stimulis"
              :combinedAnswerWithSkills="combinedAnswerWithSkills"
              @click="toggleClicked"
            />

            <div :key="`${questionKey}-answer`" class="mt-10">
              <template v-for="({ nameTagKey, answerToSkill }, answerKey, j) in answerswithSkills">
                <template v-if="answerToSkill.length || nameTagKey.length">
                  <toggled-answers
                    :key="`${questionKey}-${answerKey}-${j}`"
                    :nameTagKey="nameTagKey"
                    :answerKey="answerKey"
                    :answerToSkill="answerToSkill"
                    :toggled="toggleQuestions[questionKey]"
                  />
                </template>
              </template>
            </div>
          </template>
        </div>
      </div>

      <br />

      <sample-report :skills="skills"></sample-report>
    </template>
  </base-editor-page>
</template>

<script>
  import BaseEditorPage from '@authoringPlatform/shared/BaseEditorPage.vue';
  import SampleReport from './SampleReport.vue';
  import StimuliWithSkills from './StimuliWithSkills';
  import ToggledAnswers from './ToggledAnswers';
  import SkillsOverview from './SkillsOverview';
  import Filter from '@shared/filter.vue';
  import { mapState, mapActions } from 'vuex';
  import { getSkillScoreOverview } from '@authoringPlatform/../api/versions';
  export default {
    name: 'Scoring',

    data() {
      return {
        toggleQuestions: {},
        skillFilter: {
          selection: {},
          count: 0,
        },
        skillsOverview: {},
        isLoading: false,
      };
    },

    props: {
      versionKey: {
        type: Number,
        required: true,
      },
    },

    components: {
      SampleReport,
      BaseEditorPage,
      StimuliWithSkills,
      ToggledAnswers,
      baseFilter: Filter,
      SkillsOverview,
    },

    async mounted() {
      this.isLoading = true;
      $('[data-toggle="popover"]').popover();
      try {
        this.skillsOverview = await getSkillScoreOverview({ versionKey: this.versionKey });
      } catch (e) {
        this.notify({
          message: 'Failed to request skills overview data',
          isWarning: true,
        });
      } finally {
        this.isLoading = false;
      }
    },

    methods: {
      ...mapActions(['notify']),
      ...mapActions('editMode/score', ['getSkillScoreOverview']),
      getStimulusName(qGroupKey, authorKey, subjectTagKey) {
        let questionGroupName = this.getQuestionGroupName(qGroupKey)
          ? this.getQuestionGroupName(qGroupKey)
          : 'NA';
        let authorLastName = this.getAuthorLastName(authorKey);
        return `${questionGroupName}: ${authorLastName}_${subjectTagKey}`;
      },

      getAuthorLastName(aKey) {
        const authorNameArr = this.authors[aKey].nameTagKey.split(' ');
        return authorNameArr[authorNameArr.length - 1].trim();
      },
      getQuestionGroupName(qGroupKey) {
        return this.questionGroups[qGroupKey] ? this.questionGroups[qGroupKey].name : '';
      },
      getAnswersWithSkills(answers, FK_questionTypeKey) {
        return Object.values(answers).reduce((acc, { nameTagKey, answerKey, answerToSkill }) => {
          acc[answerKey] = {};
          acc[answerKey].nameTagKey =
            FK_questionTypeKey === 3 ? 'Participant will type out a response.' : nameTagKey;
          acc[answerKey].answerToSkill = answerToSkill;
          return acc;
        }, {});
      },

      getMinMaxForQuestion(questionKey) {
        const targetQuestion = this.questions[questionKey];
        const ansChoicesKeysArr = Object.keys(targetQuestion.answers);

        const questionSkills = Object.values(this.skills).reduce(
          (acc, { answerToSkill, skillKey }) => {
            const answersWithCurrSkill = Object.values(targetQuestion.answers).filter(
              (a) => skillKey in a.answerToSkill
            );

            let negPointsForCurrskill = [];
            if (answersWithCurrSkill.length > 0) {
              negPointsForCurrskill = answersWithCurrSkill
                .map((item) => Object.values(item.answerToSkill)[0].points)
                .filter((x) => x < 0);
            }

            if (!(skillKey in acc)) {
              acc[skillKey] = {
                // minPoint: 0,
                maxPoint: 0,
              };
            }

            if (answerToSkill.size) {
              answersWithCurrSkill.forEach(({ answerToSkill }, idx) => {
                const points = answerToSkill[skillKey].points;
                // multi-answer
                if (targetQuestion.FK_questionTypeKey === 2) {
                  acc[skillKey].maxPoint = Math.max(
                    acc[skillKey].maxPoint,
                    (acc[skillKey].maxPoint += points)
                  );
                  // acc[skillKey].minPoint = Math.min(
                  //   acc[skillKey].minPoint,
                  //   (acc[skillKey].minPoint += points)
                  // );
                } else if (targetQuestion.FK_questionTypeKey === 3) {
                  // written response
                  if (points > 0) {
                    acc[skillKey].maxPoint = points;
                    // acc[skillKey].minPoint = 0;
                  } else if (points < 0) {
                    acc[skillKey].maxPoint = 0;
                    // acc[skillKey].minPoint = points;
                  }
                } else {
                  // multiple choice
                  if (ansChoicesKeysArr.length === 1) {
                    // only one choice case
                    acc[skillKey].maxPoint = points;
                    // acc[skillKey].minPoint = points;
                  } else {
                    acc[skillKey].maxPoint = Math.max(acc[skillKey].maxPoint, points);

                    // const hasAnsWithNoSkill =
                    //   negPointsForCurrskill.length === 0 &&
                    //   answersWithCurrSkill.length < Object.values(targetQuestion.answers).length;

                    // reset min for positive points
                    // if (idx === 0 && points > 0 && !hasAnsWithNoSkill) {
                    //   acc[skillKey].minPoint = points;
                    // }
                    // acc[skillKey].minPoint = Math.min(acc[skillKey].minPoint, points);
                  }
                }
              });
            }

            return acc;
          },
          {}
        );

        return (
          Object.entries(questionSkills)
            .filter(([_, { maxPoint }]) => maxPoint)
            // .filter(([_, { minPoint, maxPoint }]) => minPoint || maxPoint)
            .reduce((acc, [skillKey, points]) => {
              acc[skillKey] = points;
              return acc;
            }, {})
        );
      },
      toggleClicked({ questionKey, toggled }) {
        this.$set(this.toggleQuestions, questionKey, toggled);
      },
      displaySkillName(name) {
        return name.length > 15 ? name.substring(0, 16) + '...' : name;
      },
      applyFilter({ skills }) {
        this.skillFilter = {
          selection: {
            ...this.skillFilter.selection,
            ...skills.selection,
          },
          count: skills.count,
        };
      },
      filterQuestions(questions, selection) {
        const newQuestionsArr = Object.entries(questions).filter(([key, { answers }]) => {
          return Object.values(answers).some(({ answerToSkill }) => {
            return Object.values(answerToSkill).some(
              ({ FK_skillKey }) => selection.hasOwnProperty(FK_skillKey) && selection[FK_skillKey]
            );
          });
        });

        const newQuestions = {};
        newQuestionsArr.forEach((value) => {
          newQuestions[value[0]] = value[1];
        });

        return newQuestions;
      },
      getQuestionTypeKey(questionKey) {
        return this.questions[questionKey].FK_questionTypeKey;
      },
    },

    computed: {
      ...mapState('editMode/version', ['versionConfig']),
      ...mapState('editMode/questions', ['questions', 'questionGroups']),
      ...mapState('editMode/skills', ['skills']),
      ...mapState('editMode/characters', {
        authors: 'characters',
      }),

      stimuliwithQuestions() {
        const { count, selection } = this.skillFilter;
        let newQuestions = this.questions;
        if (count > 0) newQuestions = this.filterQuestions(this.questions, selection);

        return Object.entries(newQuestions).reduce((acc, [key, value]) => {
          let answerswithSkills;
          let combinedAnswerWithSkills;
          const {
            answers,
            FK_questionGroupKey,
            subjectTagKey,
            FK_questionTypeKey,
            questionKey,
            FK_authorKey,
          } = value;

          let stimulis = this.getStimulusName(FK_questionGroupKey, FK_authorKey, subjectTagKey);

          if (answers) {
            answerswithSkills = this.getAnswersWithSkills(answers, FK_questionTypeKey);
            combinedAnswerWithSkills = this.getMinMaxForQuestion(questionKey);
          }
          acc[key] = { stimulis, answerswithSkills, combinedAnswerWithSkills };
          return acc;
        }, {});
      },
      scoringMethod() {
        const letter = this.versionConfig.scoringMethod
          ? this.versionConfig.scoringMethod
          : 'percentile';
        return letter ? letter.charAt(0).toUpperCase() + letter.slice(1) : ''; // first time it renders the page letter is undefined
      },
      validateCaret() {
        if (this.skills) {
          return Object.keys(this.skills).length ? 'caret' : '';
        }
      },
    },
  };
</script>
<style lang="scss" scoped>
  .scoring-indicator {
    background-color: #e5f1f7;
    text-align: center;
  }
  .table-overflow {
    height: auto;
    max-height: 600px;
    overflow-y: scroll;
    overflow-x: hidden;
  }
  .skill-flex {
    display: flex;
    flex-flow: row wrap;
  }
  .skill-size {
    width: 20%;
    text-align: left;
  }
  .font-weight-bold {
    font-weight: bold;
  }
  .border-style {
    border: 1px solid #dddddd;
  }
  .header-font-size {
    font-size: 1em;
  }
  .table-head {
    background-color: #dedede;
  }
</style>
